import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Package, Building2, Bed, ShieldCheck, Plus, Loader2,
  RefreshCw, Tag, DoorOpen, TrendingUp, Layers, ChevronDown, ChevronUp,
  Wallet, Boxes, Link2, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


function StatCard({ icon: Icon, label, value, sub, color = 'text-purple-400' }) {
  return (
    <Card className="bg-white/[0.03] border-white/5">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon className={`w-3.5 h-3.5 ${color}`} />
          <span className="text-[9px] text-gray-500 uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-lg font-bold heading-font text-white">{value}</p>
        {sub && <p className="text-[9px] text-gray-600 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function VendorCard({ vendor, onViewProducts }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid={`vendor-${vendor.vendor_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white truncate">{vendor.company_name}</h3>
              {vendor.sbt_minted && (
                <Badge variant="outline" className="text-[7px] h-3.5 border-emerald-500/20 text-emerald-400 bg-emerald-500/5 shrink-0">
                  <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> SBT
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {vendor.categories?.map(cat => (
                <Badge key={cat} variant="outline" className="text-[8px] h-4 border-purple-500/20 text-purple-400">
                  {cat}
                </Badge>
              ))}
            </div>
            {vendor.description && (
              <p className="text-[10px] text-gray-500 mb-2 line-clamp-2">{vendor.description}</p>
            )}
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/5 rounded"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
          </button>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
            {/* Token Buckets */}
            <div>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-1.5">Token Economy</p>
              <div className="grid grid-cols-3 gap-2">
                {['operations', 'staking', 'rewards'].map(bucket => (
                  <div key={bucket} className="bg-white/[0.02] rounded-lg p-2 text-center">
                    <p className="text-[8px] text-gray-500 capitalize">{bucket}</p>
                    <p className="text-xs font-bold text-[#F7931A]">{(vendor.token_buckets?.[bucket] || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Products */}
            <div>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-1.5">Products ({vendor.products?.length || 0})</p>
              {vendor.products?.length > 0 ? (
                <div className="space-y-1">
                  {vendor.products.map(p => (
                    <div key={p.product_id} className="flex items-center justify-between px-2 py-1.5 bg-white/[0.02] rounded">
                      <div>
                        <p className="text-[11px] text-gray-200">{p.name}</p>
                        <p className="text-[9px] text-gray-500">{p.category} | {p.model || p.sku || 'N/A'}</p>
                      </div>
                      <Badge variant="outline" className="text-[7px] h-3.5 border-white/10 text-gray-500 mono">
                        {p.product_id}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-gray-600">No products added yet</p>
              )}
            </div>
            {/* Room Assignments */}
            <div>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-1.5">Room Assignments ({vendor.room_assignments?.length || 0})</p>
              {vendor.room_assignments?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {vendor.room_assignments.map((ra, i) => (
                    <Badge key={i} variant="outline" className="text-[8px] h-4 border-blue-500/20 text-blue-400">
                      <DoorOpen className="w-2.5 h-2.5 mr-0.5" /> {ra.hotel_id.replace('hotel_', '')} #{ra.room_number}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-gray-600">Not assigned to any rooms yet</p>
              )}
            </div>
            {vendor.contact_email && (
              <p className="text-[9px] text-gray-500">Contact: {vendor.contact_email}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RoomSBTCard({ room }) {
  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid={`room-${room.room_key}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
            <DoorOpen className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-white">Room {room.room_number}</p>
            <p className="text-[9px] text-gray-500">{room.room_type || 'Standard'} | {room.hotel_name}</p>
          </div>
        </div>
        {room.vendor_products?.length > 0 ? (
          <div className="space-y-1">
            {room.vendor_products.map((vp, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1 bg-white/[0.02] rounded text-[10px]">
                <Tag className="w-3 h-3 text-purple-400 shrink-0" />
                <span className="text-gray-300 flex-1 truncate">{vp.product_name}</span>
                <Badge variant="outline" className="text-[7px] h-3.5 border-[#F7931A]/20 text-[#F7931A] shrink-0">
                  {vp.category}
                </Badge>
                <span className="text-[8px] text-gray-600 truncate max-w-[80px]">{vp.vendor_name}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-gray-600">No vendor products assigned</p>
        )}
      </CardContent>
    </Card>
  );
}

function RegisterVendorForm({ onRegister, registering }) {
  const [form, setForm] = useState({ company_name: '', contact_email: '', description: '', categories: [] });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/vendor/categories`).then(r => r.json()).then(d => setCategories(d.categories || []));
  }, []);

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error('Company name required'); return; }
    if (form.categories.length === 0) { toast.error('Select at least one category'); return; }
    onRegister(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-vendor-form">
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider block mb-1">Company Name *</label>
        <input
          type="text" value={form.company_name}
          onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
          className="w-full h-8 bg-white/[0.04] border border-white/8 rounded-lg px-3 text-xs text-white focus:border-purple-500/40 focus:outline-none"
          placeholder="e.g., LuxeSleep Premium Mattresses"
          data-testid="vendor-name-input"
        />
      </div>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider block mb-1">Contact Email</label>
        <input
          type="email" value={form.contact_email}
          onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
          className="w-full h-8 bg-white/[0.04] border border-white/8 rounded-lg px-3 text-xs text-white focus:border-purple-500/40 focus:outline-none"
          placeholder="sales@company.com"
          data-testid="vendor-email-input"
        />
      </div>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider block mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="w-full h-16 bg-white/[0.04] border border-white/8 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500/40 focus:outline-none resize-none"
          placeholder="Brief description of your products and services..."
          data-testid="vendor-desc-input"
        />
      </div>
      <div>
        <label className="text-[9px] text-gray-500 uppercase tracking-wider block mb-1.5">Product Categories *</label>
        <div className="flex flex-wrap gap-1.5">
          {categories.map(cat => (
            <button
              key={cat} type="button"
              onClick={() => toggleCat(cat)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                form.categories.includes(cat)
                  ? 'bg-purple-600/20 border-purple-500/30 text-purple-300'
                  : 'bg-white/[0.02] border-white/8 text-gray-500 hover:border-white/15'
              }`}
              data-testid={`cat-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
      <Button
        type="submit" className="w-full h-9 bg-[#F7931A] hover:bg-[#e8841a] text-black text-xs font-semibold"
        disabled={registering}
        data-testid="register-vendor-btn"
      >
        {registering ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
        Register Vendor & Mint SBT
      </Button>
    </form>
  );
}

export default function VendorDashboardPage() {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [tab, setTab] = useState('vendors');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, vendorsRes, roomsRes] = await Promise.all([
        fetch(`${API}/api/vendor/stats`),
        fetch(`${API}/api/vendor/list`),
        fetch(`${API}/api/vendor/rooms/hotel_grand_marina`),
      ]);
      setStats(await statsRes.json());
      const v = await vendorsRes.json();
      setVendors(v.vendors || []);
      try { const r = await roomsRes.json(); setRooms(r.rooms || []); } catch { /* no rooms yet */ }
    } catch { toast.error('Failed to load vendor data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRegister = async (form) => {
    setRegistering(true);
    try {
      const regRes = await fetch(`${API}/api/vendor/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const regData = await regRes.json();
      if (!regRes.ok) { toast.error(regData.detail || 'Registration failed'); return; }

      // Auto-mint SBT
      const mintRes = await fetch(`${API}/api/vendor/${regData.vendor.vendor_id}/mint-sbt`, { method: 'POST' });
      const mintData = await mintRes.json();
      if (mintData.success) {
        toast.success(`${form.company_name} registered with SBT #${mintData.token_id}`);
      } else {
        toast.success(`${form.company_name} registered (SBT pending)`);
      }
      setShowRegister(false);
      fetchData();
    } catch { toast.error('Registration failed'); }
    finally { setRegistering(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="vendor-dashboard">
      {/* Header */}
      <div className="border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/platform-admin" className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F7931A]/20 to-purple-500/20 border border-[#F7931A]/20 flex items-center justify-center">
                <Package className="w-4 h-4 text-[#F7931A]" />
              </div>
              <div>
                <h1 className="text-base font-bold heading-font">Vendor Protocol</h1>
                <p className="text-[10px] text-gray-500">Supply Chain SBTs & Room Inventory</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab Toggle */}
            <div className="flex items-center bg-white/[0.03] rounded-lg border border-white/5 p-0.5">
              {[
                { id: 'vendors', label: 'Vendors', icon: Building2 },
                { id: 'rooms', label: 'Room SBTs', icon: Bed },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all ${
                    tab === t.id
                      ? 'bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20'
                      : 'text-gray-500 hover:text-gray-300 border border-transparent'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  <t.icon className="w-3 h-3" />
                  {t.label}
                </button>
              ))}
            </div>
            <Link to="/vendors/revenue">
              <Button size="sm" variant="outline" className="text-xs gap-1.5 h-8 border-[#F7931A]/20 text-[#F7931A] hover:bg-[#F7931A]/5" data-testid="revenue-btn">
                <DollarSign className="w-3 h-3" /> Revenue
              </Button>
            </Link>
            <Button
              size="sm" className="text-xs gap-1.5 h-8 bg-[#F7931A] hover:bg-[#e8841a] text-black"
              onClick={() => setShowRegister(!showRegister)}
              data-testid="toggle-register-btn"
            >
              <Plus className="w-3 h-3" /> Register Vendor
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Building2} label="Vendors" value={stats?.total_vendors || 0} color="text-[#F7931A]" />
          <StatCard icon={Bed} label="Room SBTs" value={stats?.total_room_sbts || 0} color="text-blue-400" />
          <StatCard icon={Link2} label="Assignments" value={stats?.total_product_assignments || 0} color="text-purple-400" />
          <StatCard icon={Wallet} label="Tokens/Vendor" value={(stats?.token_allocation_per_vendor || 50000).toLocaleString()} sub="HERMES" color="text-emerald-400" />
        </div>

        {/* Token Bucket Split */}
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#F7931A]/[0.04] border border-[#F7931A]/10">
          <Layers className="w-4 h-4 text-[#F7931A] shrink-0" />
          <p className="text-[11px] text-gray-400 leading-relaxed">
            <span className="text-[#F7931A] font-medium">Token Economy:</span> Each vendor receives 50,000 HERMES split into 3 buckets — <span className="text-white">20% Operations</span>, <span className="text-white">60% Staking</span>, <span className="text-white">20% Rewards</span>. Room SBTs create an on-chain supply chain linking vendor products to specific hotel rooms.
          </p>
        </div>

        {/* Category breakdown */}
        {stats?.category_breakdown && Object.keys(stats.category_breakdown).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(stats.category_breakdown).map(([cat, count]) => (
              <Badge key={cat} variant="outline" className="text-[9px] h-5 border-purple-500/15 text-purple-400 gap-1">
                <Tag className="w-2.5 h-2.5" /> {cat}: {count}
              </Badge>
            ))}
          </div>
        )}

        {/* Register Form */}
        {showRegister && (
          <Card className="bg-white/[0.03] border-[#F7931A]/10">
            <CardContent className="p-4">
              <h2 className="text-sm font-semibold heading-font text-gray-200 mb-3">Register New Vendor</h2>
              <RegisterVendorForm onRegister={handleRegister} registering={registering} />
            </CardContent>
          </Card>
        )}

        {/* Vendors Tab */}
        {tab === 'vendors' && (
          <div data-testid="vendors-tab">
            <h2 className="text-sm font-semibold heading-font text-gray-300 mb-3">
              Vendors ({vendors.length})
            </h2>
            {vendors.length === 0 ? (
              <Card className="bg-white/[0.03] border-white/5">
                <CardContent className="p-8 text-center">
                  <Building2 className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No vendors registered yet</p>
                  <p className="text-[10px] text-gray-600 mt-1">Register your first vendor to start building the supply chain</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                {vendors.map(v => <VendorCard key={v.vendor_id} vendor={v} />)}
              </div>
            )}
          </div>
        )}

        {/* Rooms Tab */}
        {tab === 'rooms' && (
          <div data-testid="rooms-tab">
            <h2 className="text-sm font-semibold heading-font text-gray-300 mb-3">
              Room SBTs ({rooms.length})
            </h2>
            {rooms.length === 0 ? (
              <Card className="bg-white/[0.03] border-white/5">
                <CardContent className="p-8 text-center">
                  <Bed className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No room SBTs minted yet</p>
                  <p className="text-[10px] text-gray-600 mt-1">Assign vendor products to rooms to auto-create Room SBTs</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {rooms.map(r => <RoomSBTCard key={r.room_key} room={r} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
